// services/cart.service.ts

import {inject, Injectable, signal} from '@angular/core';
import {ApiService} from './api.service';
import {Cart, CartCreateForm, CartRemoveItemForm} from '../models/cart.model';
import {GuestCartService} from "../../shared/helpers/guest-cart/guest-cart.service";
import {createLogger} from "../../shared/helpers/logger/logger";
import {RuntimeService} from "./runtime.service";

const logger = createLogger('CartService');

@Injectable({
    providedIn: 'root',
})
export class CartService {
    cartItemCount = signal<number>(0);
    currentCart = signal<Cart | null>(null);
    readonly #runtimeService = inject(RuntimeService);

    constructor(private apiService: ApiService, private guestService: GuestCartService) {
    }

    async getCart(params?: any): Promise<{ data: Cart | null; headers: any }> {
        if (this.#runtimeService.isServer) {
            return {data: null, headers: {}};
        }
        const token = await this.apiService.getToken();
        if (!token) params = {...params, guest_token: this.guestService.getGuestToken()};
        const queryParams = this.buildQueryParams(params);
        try {
            const response = await this.apiService.get<Cart>('/cart', queryParams);
            const data = response.body;
            this.cartItemCount.set(data?.cartItems?.length || 0);
            this.currentCart.set(data);
            return {data, headers: response.headers};
        } catch (error) {
            logger.error('Error fetching cart:', error);
            throw error;
        }
    }

    async createCart(cartData: CartCreateForm): Promise<Cart> {
        const token = await this.apiService.getToken();
        if (!token) cartData.guest_token = this.guestService.getGuestToken();
        return await this.apiService.post<Cart>('/cart', cartData);
    }

    async updateCart(itemId: string, quantity: number): Promise<Cart> {
        const token = await this.apiService.getToken();
        const updateData = {
            quantity,
            guest_token: token ? null : this.guestService.getGuestToken()
        };
        try {
            const updatedCart = await this.apiService.put<Cart>(`/cart/${itemId}`, updateData);
            await this.getCart({expand: 'cartItems,cartItems.entity,cartItems.variant'});
            return updatedCart;
        } catch (error) {
            logger.error('Error updating cart:', error);
            throw error;
        }
    }

    async deleteCartItem(itemId: string): Promise<void> {
        const token = await this.apiService.getToken();
        const cartData: CartRemoveItemForm = {
            made_to_order_products: false,
            guest_token: token ? null : this.guestService.getGuestToken()
        };
        try {
            await this.apiService.delete<void>(`/cart/${itemId}`, cartData);
            await this.getCart({expand: 'cartItems,cartItems.entity,cartItems.variant'});
        } catch (error) {
            logger.error('Error deleting cart item:', error);
            throw error;
        }
    }

    incrementCartItemCount() {
        this.cartItemCount.set(this.cartItemCount() + 1);
    }

    decrementCartItemCount() {
        this.cartItemCount.set(this.cartItemCount() - 1);
    }

    async clearCart(): Promise<void> {
        const token = await this.apiService.getToken();
        const cartData = token ? {} : {guest_token: this.guestService.getGuestToken()};
        await this.apiService.delete('/cart/clear', cartData);
        this.cartItemCount.set(0);
        this.currentCart.set(null);
    }

    private buildQueryParams(params?: any): any {
        const queryParams: any = {};
        if (params) {
            ['expand', 'pageSize', 'page', 'sort', 'filter', 'made_to_order_products', 'guest_token'].forEach(key => {
                if (params[key]) queryParams[key] = params[key];
            });
        }
        return queryParams;
    }
}
